import * as Yup from "yup";
import {
  DestinationStatusEnum,
  DestinationTypeEnum,
} from "../../../enums/destination";
import {
  AllDestinations,
  ShopifyDestination,
  WordPressDestination,
} from "../../../models";

export const mask = "•";
export const secretKeyMask = mask.repeat(10);

export const initialValues: Record<DestinationTypeEnum, AllDestinations> = {
  [DestinationTypeEnum.WORDPRESS]: {
    name: "",
    destinationTypeId: DestinationTypeEnum.WORDPRESS,
    destination: {
      url: "",
      username: "",
      authorId: null,
      categoryId: null,
      secretKey: "",
      status: DestinationStatusEnum.DRAFT,
      postType: "",
      randomPostHours: null,
      postDelayHours: null,
    },
  },
  [DestinationTypeEnum.SHOPIFY]: {
    name: "",
    destinationTypeId: DestinationTypeEnum.SHOPIFY,
    destination: {
      storeName: "",
      author: "",
      blogId: "",
      secretKey: "",
      status: DestinationStatusEnum.HIDDEN,
    },
  },
};

const wordpressSchema = (isEdit: boolean) =>
  Yup.object().shape({
    name: Yup.string().required("Destination name is required"),
    destination: Yup.object().shape({
      url: Yup.string().required("Wordpress URL is required"),
      username: Yup.string().required("Admin Login Username is required"),
      secretKey: Yup.string()
        .nullable()
        .when([], {
          is: () => isEdit,
          then: (schema) => schema.notRequired().nullable(),
          otherwise: (schema) => schema.required("App password is required"),
        }),
      postType: Yup.string()
        .nullable()
        .max(20, "Custom post type slug must be at most 20 characters long")
        .matches(
          /^[a-z0-9_-]+$/,
          "Custom post type slug may only contain lowercase letters, numbers, dashes, and underscores"
        ),
    }),
  });

const shopifySchema = (isEdit: boolean) =>
  Yup.object().shape({
    name: Yup.string().required("Destination name is required"),
    destination: Yup.object().shape({
      storeName: Yup.string().required("Shopify store name is required"),
      blogId: Yup.string().required("Blog ID is required"),
      secretKey: Yup.string()
        .nullable()
        .when([], {
          is: () => isEdit,
          then: (schema) => schema.notRequired().nullable(),
          otherwise: (schema) => schema.required("App password is required"),
        }),
      author: Yup.string()
        .matches(/^[^0-9]*$/, "Author name cannot contain numbers")
        .nullable(),
    }),
  });

export const validationSchema = {
  [DestinationTypeEnum.WORDPRESS]: wordpressSchema,
  [DestinationTypeEnum.SHOPIFY]: shopifySchema,
};

export const errorMessages = {
  [DestinationTypeEnum.WORDPRESS]:
    "Wordpress plugins or security may be interfering with your site connection. Please check your settings.",
  [DestinationTypeEnum.SHOPIFY]: "Please check your settings.",
};

// TODO: Find better way to type gaurd
export function saveWithoutTesting(
  current: AllDestinations,
  original: AllDestinations
): boolean {
  const clone = structuredClone(current);

  // Allow name and status changes
  clone.name = original.name;
  clone.destination.status = original.destination.status;

  // If secretKey is empty, treat it as unchanged
  if (!clone.destination.secretKey) {
    clone.destination.secretKey = original.destination.secretKey;
  }

  // Allow author change for Shopify
  if (clone.destinationTypeId === DestinationTypeEnum.SHOPIFY) {
    (clone as ShopifyDestination).destination.author = (
      original as ShopifyDestination
    ).destination.author;
  }

  // Allow future options change for Wordpress
  if (clone.destinationTypeId === DestinationTypeEnum.WORDPRESS) {
    (clone as WordPressDestination).destination.postType = (
      original as WordPressDestination
    ).destination.postType;
    (clone as WordPressDestination).destination.postDelayHours = (
      original as WordPressDestination
    ).destination.postDelayHours;
    (clone as WordPressDestination).destination.randomPostHours = (
      original as WordPressDestination
    ).destination.randomPostHours;
  }

  return JSON.stringify(clone) === JSON.stringify(original);
}
